// The SERIES-as-predicates vocabulary for the map lab (`/_/map-lab`).
//
// The lab's first grid drew four DATASETS (one per table) and the operator's
// question was almost never "how many rows in this table": it was "chemical or
// biological", "over-rate or in-range", "parcel or right-of-way" — two KINDS
// WITHIN one table. So a series is a named PREDICATE, `table · field = value`,
// and the operator assigns up to four of them to the three roles the combined
// encodings read (Fill A, Fill B, Outline C) plus the total.
//
// This module is the catalogue and the vocabulary around it, and it is pure
// (no Vue, no store, no map) for the same reason `multi.ts`, `derived.ts` and
// `outline.ts` are: the control that builds a series, the tile that counts it
// and the key that names it must all read ONE definition of what a series IS.
// The catalogue's `sql` field is the exact predicate the tile server applies —
// `series.test.ts` reads etc/tegola.toml and pins every index to its arm, the
// same way the outline's field indexes are pinned.
//
// Byte-identity is why the series grid is its own tegola map rather than four
// more columns on `h3-multi`: an MVT layer lists its keys whether or
// not a value is null (measured on this database's PostGIS), so widening the
// multi feature would change the bytes of every existing request. The series
// grid is a pure insertion beside it.
//
// The rate VERDICT (over / in-range / under) is deliberately NOT a catalogue
// member: it is computed in Go (`platform/map_review/applied.go`) from each
// product's prescribed-rate envelope, the district's mix products, the catch
// basin classification and the unit-conversion tables — a derivation that has
// no SQL form in the tile server. Inventing an approximation would put a
// verdict in the legend that the workbench's own ledger would contradict, so
// the catalogue omits it and the control says so.

// ── Roles ──────────────────────────────────────────────────────────────────
//
// Four roles, one slot each, in this order: the slot index is the tile's
// column suffix (`n_s0`..`n_s3`), so the role decides which column a series'
// count arrives in. The colours are the Okabe–Ito set — the colourblind-safe
// palette the bivariate grid's own construction notes point at — so the three
// drawn roles stay distinguishable under protanopia, deuteranopia and
// tritanopia, and each role's ramp is a light → dark run of its own hue (no
// two classes the same colour, the lesson the bivariate palette already
// carries).

export type SeriesRoleID = "fillA" | "fillB" | "outlineC" | "total";

export interface SeriesRole {
	id: SeriesRoleID;
	/** The role's slot ordinal — and therefore the tile's column suffix, `n_s0`
	 * .. `n_s3`. It is stated rather than derived from the array's order so the
	 * role's identity in the URL cannot move if the list is ever reordered. */
	slot: number;
	label: string;
	/** The role's identity colour — the swatch, and the ramp's dark end. */
	color: string;
	/** The role's sequential ramp, light → dark, for a series in this role. */
	ramp: string[];
	/** What the role does to the encoding, in the control's words. */
	blurb: string;
}

export const SERIES_ROLES: SeriesRole[] = [
	{
		id: "fillA",
		slot: 0,
		label: "Fill A",
		color: "#0072B2",
		ramp: ["#eff6fb", "#cfe6f4", "#94c4e3", "#3b8fc4", "#005b8f"],
		blurb: "the bivariate's first axis and the difference's first side",
	},
	{
		id: "fillB",
		slot: 1,
		label: "Fill B",
		color: "#D55E00",
		ramp: ["#fdf2ea", "#fbdcc5", "#f4ab73", "#e2762a", "#a84900"],
		blurb: "the bivariate's second axis and the difference's second side",
	},
	{
		id: "outlineC",
		slot: 2,
		label: "Outline C",
		color: "#009E73",
		ramp: ["#eefaf5", "#c9eee1", "#8bd9c0", "#22b189", "#00775a"],
		blurb: "the third variable, drawn as the outline over the fill",
	},
	{
		id: "total",
		slot: 3,
		label: "Total",
		color: "#CC79A7",
		ramp: ["#fbf2f7", "#f2d7e7", "#e0a9c9", "#c46f9f", "#9c4d78"],
		blurb: "counted into the total but drawn as no axis of its own",
	},
];

/** One slot per role, and the slot index IS the role's position: the tile's
 * `n_s{slot}`. Kept as a derived number rather than a second table so the two
 * cannot drift. */
export const SERIES_SLOT_COUNT = SERIES_ROLES.length;

export function seriesRoleByID(id: SeriesRoleID): SeriesRole {
	return SERIES_ROLES.find((role) => role.id === id) ?? SERIES_ROLES[0];
}

/** The roles that draw a fill. Outline C is deliberately not one of them: it is
 * the third variable, and a series cannot be both a fill and the outline. */
export const SERIES_FILL_ROLES: SeriesRoleID[] = ["fillA", "fillB", "total"];

// ── The measure ────────────────────────────────────────────────────────────

export type SeriesMeasureID = "records" | "area" | "quantity";

export interface SeriesMeasure {
	id: SeriesMeasureID;
	/** The tile's own ordinal for this measure, pinned by the CASE arms in
	 * etc/tegola.toml (0 count, 1 SUM(area_treated), 2 SUM(quantity)). */
	index: number;
	label: string;
	/** The unit word when the data itself does not carry one. */
	unit: string;
	blurb: string;
}

export const SERIES_MEASURES: SeriesMeasure[] = [
	{
		id: "records",
		index: 0,
		label: "Records",
		unit: "records",
		blurb: "count(*) — one row, one record, today's behaviour",
	},
	{
		id: "area",
		index: 1,
		label: "Area treated",
		unit: "",
		blurb:
			"SUM(area_treated) of the series' treatment rows, in the district's area unit",
	},
	{
		id: "quantity",
		index: 2,
		label: "Quantity applied",
		unit: "",
		blurb: "SUM(quantity) of the series' treatment rows, within ONE unit",
	},
];

export function seriesMeasureByID(id: SeriesMeasureID): SeriesMeasure {
	return (
		SERIES_MEASURES.find((measure) => measure.id === id) ?? SERIES_MEASURES[0]
	);
}

/** The measure's own unit word for a readout, given the quantity unit the
 * operator picked and the district's area unit. Records read "records"; area
 * reads the district's unit; quantity reads the unit the rows were recorded
 * in. */
export function seriesMeasureUnit(
	measure: SeriesMeasureID,
	quantityUnit: string,
	areaUnit: string,
): string {
	if (measure === "area") return areaUnit;
	if (measure === "quantity")
		return quantityUnit || seriesMeasureByID("quantity").unit;
	return seriesMeasureByID(measure).unit;
}

// ── Area units ─────────────────────────────────────────────────────────────
//
// The factors platform/units converts with (`areaSqMeters`), copied rather than
// imported because that package is Go. `treatment.area_treated` is canonical
// square metres, so the tile converts the sum into the district's unit before
// it leaves the database and every number downstream — the ramp, the threshold,
// the readout — is already in that unit.

export interface AreaUnitDef {
	id: string;
	label: string;
	/** Square metres in one unit. */
	sqMeters: number;
}

export const AREA_UNITS: AreaUnitDef[] = [
	{ id: "acre", label: "acres", sqMeters: 4046.8564224 },
	{ id: "hectare", label: "hectares", sqMeters: 10000 },
	{ id: "sq ft", label: "sq ft", sqMeters: 0.09290304 },
	{ id: "sq m", label: "m²", sqMeters: 1 },
];

/** The district's preferred area unit, resolved from the organization's stored
 * preference the way platform/units resolves it: an explicit override wins, an
 * empty one inherits the preset (imperial → acre, metric → hectare). */
export function resolvedAreaUnit(
	preference: {
		preset?: string | null;
		area?: string | null;
	} | null,
): string {
	if (!preference) return "acre";
	if (preference.area) return preference.area;
	return preference.preset === "metric" ? "hectare" : "acre";
}

export function areaUnitLabel(id: string): string {
	return AREA_UNITS.find((unit) => unit.id === id)?.label ?? id;
}

// ── The catalogue ──────────────────────────────────────────────────────────
//
// Every entry is one concrete predicate, and its `index` is the ordinate the
// tile's slot CASE reads (`h3_series_s0` …). The first four are the four tables
// the combined grid has always carried — the same population, the same
// null-island exclusions, the same window — so a series grid asked for nothing
// is the multi grid's own four counts under the series grid's own column names.

export interface SeriesCatalogueEntry {
	index: number;
	id: string;
	label: string;
	/** The table(s) the predicate reads, for the control's row. */
	table: string;
	/** The column the predicate splits on, or null for the whole table. */
	field: string | null;
	value: string | null;
	/** The predicate as the tile server applies it, at this index. */
	sql: string;
	/** True when the predicate's rows carry area_treated / quantity (the
	 * treatment table's own columns). The measure does not apply to the others,
	 * and the control says so rather than drawing a silent zero. */
	measurable: boolean;
	/** What the district's data shows about this series today, where it is a
	 * finding rather than a bug. Quoted by the control beside the row. */
	note?: string;
}

export const SERIES_CATALOGUE: SeriesCatalogueEntry[] = [
	{
		index: 0,
		id: "treatment",
		label: "Treatments — the whole table",
		table: "public.treatment",
		field: null,
		value: null,
		sql: "series = 'treatment'",
		measurable: true,
	},
	{
		index: 1,
		id: "observation",
		label: "Inspections — the whole table",
		table: "public.observation ⋈ feature",
		field: null,
		value: null,
		sql: "series = 'observation'",
		measurable: false,
		note: "an observation carries no area or quantity of its own — the measure is the treatment table's",
	},
	{
		index: 2,
		id: "feature",
		label: "Features — the whole table",
		table: "public.feature",
		field: null,
		value: null,
		sql: "series = 'feature'",
		measurable: false,
		note: "a place carries no area treated or quantity of its own",
	},
	{
		index: 3,
		id: "site",
		label: "Sites — the whole table",
		table: "public.site",
		field: null,
		value: null,
		sql: "series = 'site'",
		measurable: false,
		note: "a site carries no area treated or quantity of its own",
	},

	// treatment.method — the district's own enum, three members.
	{
		index: 4,
		id: "treatment.method.chemical",
		label: "Treatments — chemical",
		table: "public.treatment",
		field: "method",
		value: "chemical",
		sql: "series = 'treatment' AND t_method = 'chemical'",
		measurable: true,
	},
	{
		index: 5,
		id: "treatment.method.biological",
		label: "Treatments — biological",
		table: "public.treatment",
		field: "method",
		value: "biological",
		sql: "series = 'treatment' AND t_method = 'biological'",
		measurable: true,
	},
	{
		index: 6,
		id: "treatment.method.physical",
		label: "Treatments — physical",
		table: "public.treatment",
		field: "method",
		value: "physical",
		sql: "series = 'treatment' AND t_method = 'physical'",
		measurable: true,
	},

	// observation · inspection.stage — the inspection vocabulary the district
	// records against an observation (one live inspection per observation).
	{
		index: 7,
		id: "observation.stage.early",
		label: "Inspections — early stage",
		table: "public.observation ⋈ feature ⋈ inspection",
		field: "inspection.stage",
		value: "early",
		sql: "series = 'observation' AND i_stage = 'early'",
		measurable: false,
	},
	{
		index: 8,
		id: "observation.stage.mixed",
		label: "Inspections — mixed stage",
		table: "public.observation ⋈ feature ⋈ inspection",
		field: "inspection.stage",
		value: "mixed",
		sql: "series = 'observation' AND i_stage = 'mixed'",
		measurable: false,
	},
	{
		index: 9,
		id: "observation.stage.late",
		label: "Inspections — late stage",
		table: "public.observation ⋈ feature ⋈ inspection",
		field: "inspection.stage",
		value: "late",
		sql: "series = 'observation' AND i_stage = 'late'",
		measurable: false,
	},

	// feature.control_state — the folded state the workbench derives from the
	// effective control window (manual revisit while it governs, else the
	// latest residual expiry), judged against now.
	{
		index: 10,
		id: "feature.control_state.controlled",
		label: "Features — controlled now",
		table: "public.feature (derived)",
		field: "control_state",
		value: "controlled",
		sql: "series = 'feature' AND f_state = 'controlled'",
		measurable: false,
	},
	{
		index: 11,
		id: "feature.control_state.expired",
		label: "Features — control expired",
		table: "public.feature (derived)",
		field: "control_state",
		value: "expired",
		sql: "series = 'feature' AND f_state = 'expired'",
		measurable: false,
	},
	{
		index: 12,
		id: "feature.control_state.unknown",
		label: "Features — control unknown",
		table: "public.feature (derived)",
		field: "control_state",
		value: "unknown",
		sql: "series = 'feature' AND f_state = 'unknown'",
		measurable: false,
	},

	// site.kind — the district's own enum, three members.
	{
		index: 13,
		id: "site.kind.parcel",
		label: "Sites — parcel",
		table: "public.site",
		field: "kind",
		value: "parcel",
		sql: "series = 'site' AND s_kind = 'parcel'",
		measurable: false,
	},
	{
		index: 14,
		id: "site.kind.street",
		label: "Sites — street",
		table: "public.site",
		field: "kind",
		value: "street",
		sql: "series = 'site' AND s_kind = 'street'",
		measurable: false,
	},
	{
		index: 15,
		id: "site.kind.other",
		label: "Sites — other",
		table: "public.site",
		field: "kind",
		value: "other",
		sql: "series = 'site' AND s_kind = 'other'",
		measurable: false,
	},
];

export const SERIES_CATALOGUE_SIZE = SERIES_CATALOGUE.length;

export function seriesByIndex(index: number): SeriesCatalogueEntry {
	return (
		SERIES_CATALOGUE.find((entry) => entry.index === index) ??
		SERIES_CATALOGUE[0]
	);
}

export function seriesByID(id: string): SeriesCatalogueEntry | null {
	return SERIES_CATALOGUE.find((entry) => entry.id === id) ?? null;
}

/** The four table series — the catalogue's first four entries, and the default
 * content of the four slots. */
export const SERIES_TABLE_INDICES: number[] = [0, 1, 2, 3];

// ── The control's vocabulary ───────────────────────────────────────────────

/** One field the SEARCH side of the control offers: a column with a small,
 * closed member list, which is what makes it a fill rather than a ranking. */
export interface SeriesFieldDef {
	/** Stable id, used by the control's two dropdowns. */
	id: string;
	label: string;
	/** The table the control's row names. */
	table: string;
	/** False when the rows carry no area_treated / quantity. */
	measurable: boolean;
	/** The catalogue index of each member, in the enum's own order, so a pick
	 * cannot name an index the tile does not have an arm for. */
	members: { value: string; label: string; index: number }[];
	/** The district's data shows this field is empty today. */
	note?: string;
}

export interface SeriesTableDef {
	id: string;
	label: string;
	/** The whole table as one series (no split) — the catalogue's own entry. */
	whole: number;
	fields: SeriesFieldDef[];
}

export const SERIES_TABLES: SeriesTableDef[] = [
	{
		id: "treatment",
		label: "Treatments",
		whole: 0,
		fields: [
			{
				id: "method",
				label: "Method — treatment.method",
				table: "public.treatment",
				measurable: true,
				members: [
					{ value: "chemical", label: "chemical", index: 4 },
					{ value: "biological", label: "biological", index: 5 },
					{ value: "physical", label: "physical", index: 6 },
				],
				note: "org 1 records a method on 786 of 116,833 live treatments (all physical); org 124 is the populated district (chemical 156,472, biological 16,148)",
			},
		],
	},
	{
		id: "observation",
		label: "Inspections (observations)",
		whole: 1,
		fields: [
			{
				id: "inspection.stage",
				label: "Inspection stage — inspection.stage",
				table: "public.observation ⋈ feature ⋈ inspection",
				measurable: false,
				members: [
					{ value: "early", label: "early", index: 7 },
					{ value: "mixed", label: "mixed", index: 8 },
					{ value: "late", label: "late", index: 9 },
				],
				note: "the stage of the observation's live inspection; 39,850 org-1 inspections carry no stage",
			},
		],
	},
	{
		id: "feature",
		label: "Features",
		whole: 2,
		fields: [
			{
				id: "control_state",
				label: "Control state — derived",
				table: "public.feature (derived)",
				measurable: false,
				members: [
					{ value: "controlled", label: "controlled", index: 10 },
					{ value: "expired", label: "expired", index: 11 },
					{ value: "unknown", label: "unknown", index: 12 },
				],
				note: "every Feature is 'unknown' today: no Feature carries a revisit date and no Treatment a residual expiry",
			},
		],
	},
	{
		id: "site",
		label: "Sites",
		whole: 3,
		fields: [
			{
				id: "kind",
				label: "Kind — site.kind",
				table: "public.site",
				measurable: false,
				members: [
					{ value: "parcel", label: "parcel", index: 13 },
					{ value: "street", label: "street", index: 14 },
					{ value: "other", label: "other", index: 15 },
				],
				note: "all 16,226 org-1 sites are 'other'; parcel and street are empty",
			},
		],
	},
];

/** The fields the control deliberately does NOT offer, and why. A field whose
 * member count is not a small closed set is a RANKING: colouring thirty-eight
 * products or ninety applicators as fills would put thirty-eight classes in a
 * key nobody can read, and no two of them would be distinguishable. The control
 * names them instead of hiding them. */
export interface SeriesRankingField {
	table: string;
	field: string;
	reason: string;
}

export const SERIES_RANKING_FIELDS: SeriesRankingField[] = [
	{
		table: "public.treatment",
		field: "product",
		reason:
			"~38 live products — a ranking, not a fill: two products would share a colour",
	},
	{
		table: "public.treatment",
		field: "creator (applicator)",
		reason: "~90 live users — a ranking, not a fill",
	},
	{
		table: "public.inspection",
		field: 'method ("took N dips")',
		reason: "unbounded text, one label per dip count — not a closed vocabulary",
	},
	{
		table: "public.observation",
		field: "condition",
		reason: "11 live values, several compound — a ranking, not a fill",
	},
];

/** The district's enum list for one field, the same lists the ledger's enum
 * filter offers. */
export function seriesFieldOf(
	tableID: string,
	fieldID: string,
): SeriesFieldDef | null {
	const table = SERIES_TABLES.find((entry) => entry.id === tableID);
	return table?.fields.find((field) => field.id === fieldID) ?? null;
}

// ── Slots ──────────────────────────────────────────────────────────────────

/** One assigned series: which role draws it, which catalogue entry it is, and
 * the label and colour the operator gave it (auto-written from the picks, then
 * editable). */
export interface SeriesSlot {
	role: SeriesRoleID;
	index: number;
	label: string;
	color: string;
}

/** One slot's per-cell field name, in the measure's units: the count always
 * arrives as `n_s{slot}` and the measure's sum as `m_s{slot}`, so the client
 * picks the column its measure reads and the tile carries both. */
export function seriesSlotField(
	slot: number,
	measure: SeriesMeasureID,
): string {
	return measure === "records" ? `n_s${slot}` : `m_s${slot}`;
}

/** The tile's slot parameter names, and the measure's own. Kept here so the
 * grid's URL builder and the dissolve's cannot drift. */
export const SERIES_PARAM_SLOT = (slot: number): string => `h3_series_s${slot}`;
export const SERIES_PARAM_MEASURE = "h3_series_measure";
export const SERIES_PARAM_UNIT = "h3_series_unit";
export const SERIES_PARAM_AREA_UNIT = "h3_series_area_unit";
/** A slot's OWN metric and quantity unit — the saved layers' extension of the
 * one global `measure`/`unit` the lab's four slots shared. Both default to the
 * globals server-side, so a request that states neither is unchanged. */
export const SERIES_PARAM_MEASURE_SLOT = (slot: number): string =>
	`h3_series_m${slot}`;
export const SERIES_PARAM_UNIT_SLOT = (slot: number): string =>
	`h3_series_u${slot}`;

/** The measure as one clause, with its unit — the half of the legend's
 * sentence that every series shares. */
export function measureSentence(
	measure: SeriesMeasureID,
	quantityUnit: string,
	areaUnit: string,
): string {
	const unit = seriesMeasureUnit(measure, quantityUnit, areaUnit);
	if (measure === "records") return "counting records (one row, one record)";
	if (measure === "area") return `summing the area treated in ${unit}`;
	return `summing the quantity applied in ${unit}`;
}

/** The plain-language legend SENTENCE the slots derive — how the operator can
 * read what they symbolized without parsing the key. It names the fill's two
 * axes, the outline and the total, states the measure with its unit, and — the
 * honesty rule — names every assigned series the district's data does not have
 * rather than leaving the operator with a blank map. */
export function seriesLegendSentence(input: {
	slots: SeriesSlot[];
	measure: SeriesMeasureID;
	quantityUnit: string;
	areaUnit: string;
	/** Per catalogue index, the window total the server counted — omitted while
	 * the count is still in flight, so a missing number is not read as zero. */
	totals?: Record<number, number>;
}): string {
	const { slots, measure, quantityUnit, areaUnit } = input;
	if (slots.length === 0) return "";
	const roleOf = (role: SeriesRoleID) =>
		slots.find((entry) => entry.role === role) ?? null;
	const empty = slots
		.filter((slot) => input.totals?.[slot.index] === 0)
		.map((slot) => `“${slot.label}”`);
	const a = roleOf("fillA");
	const b = roleOf("fillB");
	const c = roleOf("outlineC");
	const t = roleOf("total");
	const clauses: string[] = [];
	if (a || b)
		clauses.push(
			`fill: ${a ? `“${a.label}” (left)` : "nothing"} × ${b ? `“${b.label}” (up)` : "nothing"}`,
		);
	if (c) clauses.push(`outline: “${c.label}”`);
	if (t) clauses.push(`total adds “${t.label}”`);
	const body = clauses.join("; ");
	const measureClause = measureSentence(measure, quantityUnit, areaUnit);
	const empties =
		empty.length === 0
			? ""
			: ` — EMPTY in this district and window: ${empty.join(", ")}`;
	return `${body.charAt(0).toUpperCase()}${body.slice(1)}; ${measureClause}.${empties}`;
}

// ── The tile ───────────────────────────────────────────────────────────────

/** The series grid's own tegola map. A SEPARATE map from `h3-multi`
 * on purpose: an MVT feature lists its keys whether or not the value is null,
 * so four more columns on the multi grid would change the bytes of every
 * existing request. A new map is a pure insertion and leaves the multi grid's
 * bytes untouched. */
export const SERIES_MAP = "h3-series";
export const SERIES_SOURCE_LAYER = "service-area-h3-series";
export const SERIES_PARAM_RESOLUTION = "h3_series_resolution";
export const SERIES_PARAM_FROM = "h3_series_from";
export const SERIES_PARAM_TO = "h3_series_to";

export interface SeriesTileInput {
	base: string;
	resolution: number;
	from: string;
	to: string;
	/** The four slot assignments, in role order. An unassigned slot is null and
	 * the tile leaves its column null (parameter omitted). */
	slots: (number | null)[];
	measure: SeriesMeasureID;
	/** The quantity unit, when the measure is quantity. */
	quantityUnit: string;
	/** The district's area unit, when the measure is area. */
	areaUnit: string;
	/** Per-slot metric overrides, index = slot: a saved H3 layer's variable
	 * carries its OWN metric where the lab's four slots shared one. A null entry
	 * leaves that slot on the global `measure`, so an input without this array
	 * (every lab caller) builds exactly today's URL. */
	measures?: (SeriesMeasureID | null)[];
	/** Per-slot quantity units, index = slot, read by the slot whose `measures`
	 * entry is quantity. An empty or null entry leaves the slot on the global
	 * `unit`. */
	units?: (string | null)[];
}

/** The series grid's tile template for one rung. Unlike the multi grid's — whose
 * signature deliberately takes no encoding so a mode switch is a repaint — this
 * one takes the whole encoding: the slots and the measure ARE the query, and a
 * change of slot or measure is a new request. That is the price of the answer
 * being a different question, not a different colour. */
export function seriesTiles(input: SeriesTileInput): string[] {
	if (!input.base) return [];
	const params = new URLSearchParams();
	params.set(SERIES_PARAM_RESOLUTION, String(input.resolution));
	if (input.from) params.set(SERIES_PARAM_FROM, input.from);
	if (input.to) params.set(SERIES_PARAM_TO, input.to);
	input.slots.forEach((index, slot) => {
		if (index === null) return;
		params.set(SERIES_PARAM_SLOT(slot), String(index));
	});
	if (input.measure !== "records")
		params.set(
			SERIES_PARAM_MEASURE,
			String(seriesMeasureByID(input.measure).index),
		);
	if (input.measure === "quantity" && input.quantityUnit)
		params.set(SERIES_PARAM_UNIT, input.quantityUnit);
	// A slot's OWN metric and unit, when a saved layer gave it one: an entry
	// that is null leaves the slot on the globals above (slot 3's rule), which
	// is what keeps every lab URL identical.
	input.measures?.forEach((measure, slot) => {
		if (!measure) return;
		params.set(
			SERIES_PARAM_MEASURE_SLOT(slot),
			String(seriesMeasureByID(measure).index),
		);
	});
	input.units?.forEach((unit, slot) => {
		if (!unit) return;
		params.set(SERIES_PARAM_UNIT_SLOT(slot), unit);
	});
	// The area unit is the DISTRICT's, so it stays one param however many slots
	// measure acreage — but a per-slot area metric no longer sets the global
	// `measure`, so the unit has to travel on its own account as well.
	if (input.measure === "area" || input.measures?.includes("area"))
		params.set(SERIES_PARAM_AREA_UNIT, input.areaUnit);
	return [`${input.base}maps/${SERIES_MAP}/{z}/{x}/{y}?${params}`];
}

/** The slot ordinal each role's series is assigned to, or null when the role is
 * unassigned — the tile's parameters are built from this, and it is the one
 * place the role → column-suffix mapping is applied. */
export function slotOrdinals(slots: SeriesSlot[]): (number | null)[] {
	const ordinals: (number | null)[] = SERIES_ROLES.map(() => null);
	for (const slot of slots)
		ordinals[seriesRoleByID(slot.role).slot] = slot.index;
	return ordinals;
}
